import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'
import { v1 as uuidv1 } from 'uuid'

import {
  ArtistMembershipForGuestQuery,
  ArtistMembershipForGuestQueryVariables,
  ArtistMembershipQuery,
  ArtistMembershipQueryVariables,
  ArtistMembershipSubscriptionDetailFragmentDoc,
  ArtistWithSubscriptionFragmentDoc,
  CancelDowngradeSubscriptionPlanMutation,
  CancelDowngradeSubscriptionPlanMutationVariables,
  CreateIapSubscriptionMutation,
  CreateIapSubscriptionMutationVariables,
  DowngradeSubscriptionPlanMutation,
  DowngradeSubscriptionPlanMutationVariables,
  Maybe,
  MembershipSubscription,
  PatronDetailFragmentDoc,
  PaymentDetailFragmentDoc,
  PreviewSubscriptionUpdateQuery,
  PreviewSubscriptionUpdateQueryVariables,
  StopSubscription,
  StopSubscriptionMutation,
  StopSubscriptionMutationVariables,
  TryPaymentLockMutation,
  TryPaymentLockMutationVariables,
  UpgradeSubscriptionPlanMutation,
  UpgradeSubscriptionPlanMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { getActiveMembershipSubscriptionProductPlan } from '@/core/membership'
import { useSettingUserInfoModal } from '@/core/membership/useSettingUserInfoModal'
import { App } from '@/core/native'
import { getPlatform } from '@/core/native/app'
import { setRegisterNewMembership } from '@/core/native/storage'
import { getSubscriptionById, purchase } from '@/core/payment'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router } = useRouter<{ artistId: string }>()
  const { t } = useTranslation('artistMembership')
  const { showModal } = useSettingUserInfoModal()
  const artistId = router.query.artistId ?? config.artistId

  const isWeb = App.getPlatform() === 'web'
  const isIOs = App.getPlatform() === 'ios'

  const [nativeSubscribing, setNativeSubscribing] = useState(false)

  const { data, loading } = useQuery<
    ArtistMembershipQuery,
    ArtistMembershipQueryVariables
  >(QUERY, {
    variables: { id: artistId },
    skip: !user,
  })
  const { data: dataGuest, loading: loadingGuest } = useQuery<
    ArtistMembershipForGuestQuery,
    ArtistMembershipForGuestQueryVariables
  >(QUERY_FOR_GUEST, {
    variables: { id: artistId },
    skip: !!user,
  })
  const [
    previewSubscriptionUpdate,
    {
      data: previewSubscriptionUpdateData,
      loading: fetchingDiffAmountSubscription,
    },
  ] = useLazyQuery<
    PreviewSubscriptionUpdateQuery,
    PreviewSubscriptionUpdateQueryVariables
  >(PREVIEW_SUBSCRIPTION_UPDATE)

  const [upgradeSubscriptionPlan, { loading: upgradingSubscriptionPlan }] =
    useMutation<
      UpgradeSubscriptionPlanMutation,
      UpgradeSubscriptionPlanMutationVariables
    >(UPGRADE_SUBSCRIPTION_PLAN, {
      refetchQueries: () => [{ query: QUERY, variables: { id: artistId } }],
      awaitRefetchQueries: true,
    })

  const [downgradeSubscriptionPlan, { loading: downgradingSubscriptionPlan }] =
    useMutation<
      DowngradeSubscriptionPlanMutation,
      DowngradeSubscriptionPlanMutationVariables
    >(DOWNGRADE_SUBSCRIPTION_PLAN, {
      refetchQueries: () => [{ query: QUERY, variables: { id: artistId } }],
      awaitRefetchQueries: true,
    })

  const [
    cancelDowngradeSubscriptionPlan,
    { loading: cancelingDowngradeSubscriptionPlan },
  ] = useMutation<
    CancelDowngradeSubscriptionPlanMutation,
    CancelDowngradeSubscriptionPlanMutationVariables
  >(CANCEL_DOWNGRADE_SUBSCRIPTION_PLAN, {
    refetchQueries: () => [{ query: QUERY, variables: { id: artistId } }],
    awaitRefetchQueries: true,
  })

  const [stopSubscription, { loading: stoppingSubscription }] = useMutation<
    StopSubscriptionMutation,
    StopSubscriptionMutationVariables
  >(StopSubscription, {
    refetchQueries: () => [{ query: QUERY, variables: { id: artistId } }],
    awaitRefetchQueries: true,
  })

  const [tryPaymentLock] = useMutation<
    TryPaymentLockMutation,
    TryPaymentLockMutationVariables
  >(TRY_PAYMENT_LOCK)
  const [createIapSubscription] = useMutation<
    CreateIapSubscriptionMutation,
    CreateIapSubscriptionMutationVariables
  >(CREATE_IAP_SUBSCRIPTION, {
    async onCompleted({ createIapSubscription }) {
      await setRegisterNewMembership()
      if (createIapSubscription?.isPersonalInformationRequired) {
        showModal({
          showSnackbarWhenClose: true,
          isRegisterNewMembership: true,
        })
      }
    },
  })

  const { artist } = (data ?? dataGuest) || {}

  const appName = useMemo<string | undefined>(
    /**
     * @description
     * Open when function is implemented in the future
     * https://app.clickup.com/t/908127/UT-15031
     */
    //@ts-ignore
    () => getAppNameByPlatformAndSubscription(artist?.subscription),
    [artist?.subscription]
  )

  /**
   * サブスク契約（ネイティブ用）
   */
  const subscribeByNative = async () => {
    setNativeSubscribing(true)

    // revenueCatより該当アーティストのサブスク情報を取得
    const apiKey = App.getPlatformResult({
      ios: config.revenueCatApiKey.ios,
      android: config.revenueCatApiKey.android,
      web: '',
    })
    const productId = await getSubscriptionById(
      artist?.id || '',
      apiKey,
      user?.uid || '',
      isIOs
    )

    // ランダム生成したidでトランザクションのロックをかける
    const uuid = uuidv1()
    const { data: tryLockForNativePurchase } = await tryPaymentLock({
      variables: { uuid },
    })

    if (!tryLockForNativePurchase?.tryPaymentLock?.success)
      throw new Error('tryLockForNativePurchaseError')

    // ネイティブ機能でrevenueCat経由でサブスク契約
    await purchase(productId, apiKey, user?.uid, true, t)

    const { data: tryLockForSync } = await tryPaymentLock({
      variables: { uuid },
    })

    if (!tryLockForSync?.tryPaymentLock?.success)
      throw new Error('tryLockForSyncError')

    // mutationでfirestore側へ決済完了を同期する

    /**
     * @description
     * iOS: liteとcrazy vodka tonicはUtoniqのiOS/revenueCatプロジェクトサブスクリプション登録してあるので、
     * 上記のパターンの時のみappにutoniqを入れる。
     */
    const app =
      isIOs &&
      (config.applicationKey === 'lite' ||
        config.applicationKey === 'crazyvodkatonic')
        ? 'utoniq'
        : config.applicationKey
    return createIapSubscription({
      variables: { productId: productId, uuid, app, artistId },
      refetchQueries: () => [{ query: QUERY, variables: { id: artistId } }],
      awaitRefetchQueries: true,
    })
  }

  const activeProduct = useMemo(() => {
    return getActiveMembershipSubscriptionProductPlan(
      data?.artist,
      data?.artist?.pendingPurchasePatron?.metadata?.membership?.planId ??
        data?.artist?.patron?.subscription?.planId
    )
  }, [data?.artist])

  const downgradeProduct = useMemo(() => {
    return getActiveMembershipSubscriptionProductPlan(
      data?.artist,
      data?.artist?.patron?.subscription?.downgradePlanId
    )
  }, [data?.artist])

  return {
    state: {
      isLoggedIn: !!user,
      loading: loading || loadingGuest,
      subscribing:
        upgradingSubscriptionPlan ||
        downgradingSubscriptionPlan ||
        cancelingDowngradeSubscriptionPlan ||
        stoppingSubscription ||
        nativeSubscribing,
      isMember: !!artist?.patron,
      patronType: artist?.patron?.type ?? null,
      store: artist?.patron?.iapSubscription?.store ?? null,
      /**
       * @description
       * Open when function is implemented in the future
       * https://app.clickup.com/t/908127/UT-15031
       */
      //@ts-ignore
      stopSubscription: artist?.subscription?.status === 'stopped',
      requireRegisteredCredit: isWeb && !data?.paymentSource && !!user,
      artist,
      appName,
      subscription: artist?.patron?.subscription ?? null,
      downgradeAtNextPeriod: !!artist?.patron?.downgradeAtNextPeriod,
      products:
        data?.artist?.subscription?.products ??
        dataGuest?.artist?.subscription?.products ??
        null,
      activeProduct,
      downgradeProduct,
      isPersonalInformationRequired:
        !!artist?.patron?.isPersonalInformationRequired,
      isMembershipPersonalInformationRequired:
        !!data?.user?.isMembershipPersonalInformationRequired,
      expiredAtSubscription: artist?.patron?.formattedExpiredAt ?? null,
      pendingPurchasePatron: artist?.pendingPurchasePatron ?? null,
      diffAmountSubscription:
        previewSubscriptionUpdateData?.previewSubscriptionUpdate.diffAmount ??
        0,
      fetchingDiffAmountSubscription,
    },
    actions: {
      // アップデート時に支払う日割差額を取得する
      async onPreviewSubscriptionUpdate(planId) {
        await previewSubscriptionUpdate({
          variables: { artistId, planId },
        })
      },
      // サブスクを開始する
      async onSubscribePlan() {
        if (isWeb) {
          return
        }
        await subscribeByNative().finally(() => setNativeSubscribing(false))
      },
      // 上位プランへのアップグレード
      async onUpgradePlan(planId) {
        await upgradeSubscriptionPlan({
          variables: { artistId, planId },
        })
      },
      // 下位プランへのダウングレード
      async onDowngradePlan(planId) {
        await downgradeSubscriptionPlan({
          variables: { artistId, planId },
        })
      },
      // サブスク自体の停止
      async onStopSubscription() {
        await stopSubscription({
          variables: { artistId },
        })
      },
      // ダウングレードのキャンセル
      async onCancelDowngradePlan() {
        await cancelDowngradeSubscriptionPlan({
          variables: { artistId },
        })
      },
    },
  }
})

const QUERY = gql`
  query ArtistMembership($id: ID) {
    artist(id: $id) {
      ...ArtistWithSubscription
      ...ArtistMembershipSubscriptionDetail
    }
    user {
      id
      isMembershipPersonalInformationRequired
    }
    paymentSource {
      ...PaymentDetail
    }
  }
  ${ArtistWithSubscriptionFragmentDoc}
  ${ArtistMembershipSubscriptionDetailFragmentDoc}
  ${PaymentDetailFragmentDoc}
`

const PREVIEW_SUBSCRIPTION_UPDATE = gql`
  query PreviewSubscriptionUpdate($planId: String!, $artistId: String!) {
    previewSubscriptionUpdate(planId: $planId, artistId: $artistId) {
      planId
      diffAmount
    }
  }
`

const QUERY_FOR_GUEST = gql`
  query ArtistMembershipForGuest($id: ID) {
    artist(id: $id) {
      ...ArtistWithSubscription
      ...ArtistMembershipSubscriptionDetail
    }
  }
  ${ArtistWithSubscriptionFragmentDoc}
  ${ArtistMembershipSubscriptionDetailFragmentDoc}
`

const START_SUBSCRIPTION = gql`
  mutation startSubscription($artistId: ID!, $planId: ID!) {
    startSubscription(artistId: $artistId, planId: $planId) {
      ...PatronDetail
    }
  }
  ${PatronDetailFragmentDoc}
`

const UPGRADE_SUBSCRIPTION_PLAN = gql`
  mutation upgradeSubscriptionPlan(
    $planId: String!
    $paymentMethod: PaymentMethod
    $artistId: String!
  ) {
    upgradeSubscriptionPlan(
      planId: $planId
      paymentMethod: $paymentMethod
      artistId: $artistId
    ) {
      ...PatronDetail
    }
  }
  ${PatronDetailFragmentDoc}
`

const DOWNGRADE_SUBSCRIPTION_PLAN = gql`
  mutation downgradeSubscriptionPlan(
    $planId: String!
    $paymentMethod: PaymentMethod
    $artistId: String!
  ) {
    downgradeSubscriptionPlan(
      planId: $planId
      paymentMethod: $paymentMethod
      artistId: $artistId
    ) {
      ...PatronDetail
    }
  }
  ${PatronDetailFragmentDoc}
`

const CANCEL_DOWNGRADE_SUBSCRIPTION_PLAN = gql`
  mutation cancelDowngradeSubscriptionPlan($artistId: String!) {
    cancelDowngradeSubscriptionPlan(artistId: $artistId) {
      ...PatronDetail
    }
  }
  ${PatronDetailFragmentDoc}
`

const TRY_PAYMENT_LOCK = gql`
  mutation tryPaymentLock($uuid: String!) {
    tryPaymentLock(uuid: $uuid) {
      success
    }
  }
`

const CREATE_IAP_SUBSCRIPTION = gql`
  mutation createIapSubscription(
    $productId: String!
    $uuid: String
    $app: String
    $artistId: String
  ) {
    createIapSubscription(
      productId: $productId
      uuid: $uuid
      app: $app
      artistId: $artistId
    ) {
      ...PatronDetail
    }
  }
  ${PatronDetailFragmentDoc}
`

/**
 * @description return app name by platform and subscription
 * if platform = web return undefined
 * if platform = android and playstore not existed return app name
 * if platform = ios and appstore not existed return app name
 * @param {MembershipSubscription} subscription
 */
export function getAppNameByPlatformAndSubscription(
  subscription?: Maybe<MembershipSubscription>
): string | undefined {
  const platform = getPlatform()
  const url = new URL(config.deepLinkUrl)
  if (platform === 'web') return undefined
  // @note productsは配列だが、ネイティブは現状1プランしか存在できないため、配列の先頭をとる
  if (
    platform === 'android' &&
    !subscription?.products?.[0]?.plans.some(plan => !!plan.playstore)
  ) {
    return url.host
  }
  if (
    platform === 'ios' &&
    !subscription?.products?.[0]?.plans.some(plan => !!plan.appstore)
  ) {
    return url.host
  }
  return undefined
}
